import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Play, CheckCircle2, XCircle, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  scheduled: "bg-[#1E88E5]/10 text-[#1E88E5]",
  in_progress: "bg-[#FFB400]/10 text-[#FFB400]",
  completed: "bg-[#16A34A]/10 text-[#16A34A]",
  cancelled: "bg-[#DC2626]/10 text-[#DC2626]",
};

const AdminJobs = () => {
  const [items, setItems] = useState([]);
  const [workers, setWorkers] = useState({});
  const [customers, setCustomers] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [jobsRes, wsRes, csRes] = await Promise.all([
      api.get("/admin/jobs"),
      api.get("/admin/workers"),
      api.get("/admin/customers"),
    ]);
    setItems(jobsRes.data.items);
    const wm = {}; wsRes.data.items.forEach((w) => { wm[w.id] = w; }); setWorkers(wm);
    const cm = {}; csRes.data.items.forEach((c) => { cm[c.id] = c; }); setCustomers(cm);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const action = async (id, verb) => {
    setBusy(`${id}:${verb}`);
    try {
      await api.patch(`/admin/jobs/${id}/${verb}`);
      toast.success(`Job ${verb === "complete" ? "completed — review request sent on WhatsApp 🎉" : verb + "ed"}`);
      await load();
    } catch {
      toast.error("Action failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold text-[#2D3748]">Jobs</h1>
      <p className="text-[#718096] mt-1">Jobs auto-created when a quote is approved.</p>
      <div className="mt-6 bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-[#718096]">No jobs yet — approve a quote in the Quotes tab to auto-create one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F7FAFC] text-[#718096] uppercase tracking-wider text-xs">
              <tr>
                <th className="text-left px-5 py-3">Customer / Worker</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Timeline</th>
                <th className="text-left px-5 py-3">Review</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {items.map((j) => {
                const w = workers[j.worker_id];
                const c = customers[j.customer_id];
                const isBusy = (verb) => busy === `${j.id}:${verb}`;
                return (
                  <tr key={j.id} className="hover:bg-[#F7FAFC]" data-testid={`job-row-${j.id}`}>
                    <td className="px-5 py-4">
                      <div className="font-heading font-semibold text-[#2D3748]">{c?.name || "—"}</div>
                      <div className="text-xs text-[#718096]">→ {w?.business_name || "—"}</div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_STYLES[j.status]}`} data-testid={`job-status-${j.id}`}>
                        {j.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-[#718096]">
                      {j.started_at ? <>Started: {new Date(j.started_at).toLocaleDateString()}<br/></> : null}
                      {j.completed_at ? <>Done: {new Date(j.completed_at).toLocaleDateString()}</> : null}
                      {!j.started_at && !j.completed_at && <>Scheduled {new Date(j.created_at).toLocaleDateString()}</>}
                    </td>
                    <td className="px-5 py-4">
                      {j.review_received_review_id ? (
                        <span className="inline-flex items-center gap-1 text-[#16A34A] text-xs"><Star className="w-3.5 h-3.5 fill-[#FFB400] text-[#FFB400]" /> received</span>
                      ) : j.review_requested_at ? (
                        <span className="text-xs text-[#FFB400]">requested</span>
                      ) : (
                        <span className="text-xs text-[#718096]">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right space-x-2 whitespace-nowrap">
                      {j.status === "scheduled" && (
                        <Button size="sm" disabled={isBusy("start")} onClick={() => action(j.id, "start")} data-testid={`start-job-${j.id}`} className="rounded-full text-xs bg-[#1E88E5] hover:bg-[#1565C0] text-white">
                          {isBusy("start") ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-1" />} Start
                        </Button>
                      )}
                      {j.status === "in_progress" && (
                        <Button size="sm" disabled={isBusy("complete")} onClick={() => action(j.id, "complete")} data-testid={`complete-job-${j.id}`} className="rounded-full text-xs bg-[#25D366] hover:bg-[#16A34A] text-white">
                          {isBusy("complete") ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Complete
                        </Button>
                      )}
                      {(j.status === "scheduled" || j.status === "in_progress") && (
                        <Button size="sm" disabled={isBusy("cancel")} variant="outline" onClick={() => action(j.id, "cancel")} data-testid={`cancel-job-${j.id}`} className="rounded-full text-xs">
                          <XCircle className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminJobs;
